import { useCallback, useEffect, useState } from "react"
import { usePropertyContext } from "@/lib/usePropertyContext"
import {
  apiClient,
  type InsightResult,
  type PerformanceSummaryResponse,
  type ReviewIntelligenceResponse,
  type SeasonContext,
} from "@/lib/api"
import type { OverviewPayload, OverviewResponse } from "./types"

export type InsightsStatus = "ok" | "failed" | "loading"
export type ResourceStatus = "idle" | "loading" | "ok" | "failed"

export interface OverviewPropertySettingsSummary {
  wifiName: string | null
  wifiPasswordConfigured: boolean
  doorCodeConfigured: boolean
  doorCodeShowGuidebook: boolean
  doorCodeShowDisplay: boolean
  doorCodeOffsetMinutes: number | null
  guidebookPinConfigured: boolean
  guidebookTemplate: string | null
  renderKey: string | null
}

export interface OverviewHouseRulesSummary {
  enabledCoreRules: number
  displayRules: number
  guidebookRules: number
  quietHoursConfigured: boolean
  checkoutConfigured: boolean
  summary: string | null
  updatedAt: string | null
}

interface UseOverviewDataReturn {
  overview: OverviewPayload | null
  insights: InsightResult | null
  insightsStatus: InsightsStatus
  seasonContext: SeasonContext | null
  performanceSummary: PerformanceSummaryResponse | null
  performanceStatus: ResourceStatus
  reviewIntelligence: ReviewIntelligenceResponse | null
  reviewStatus: ResourceStatus
  propertySettings: OverviewPropertySettingsSummary | null
  propertySettingsStatus: ResourceStatus
  houseRules: OverviewHouseRulesSummary | null
  houseRulesStatus: ResourceStatus
  loading: boolean
  error: string
  refresh: () => void
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  return value as Record<string, unknown>
}

function asString(value: unknown): string | null {
  if (typeof value !== "string") return null
  const next = value.trim()
  return next ? next : null
}

function asBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const next = value.trim().toLowerCase()
    if (!next) return fallback
    if (next === "true" || next === "1" || next === "yes") return true
    if (next === "false" || next === "0" || next === "no") return false
  }
  return fallback
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

function readPath(source: Record<string, unknown> | null, path: string[]): unknown {
  let current: unknown = source
  for (const key of path) {
    const next = asRecord(current)
    if (!next || !(key in next)) return undefined
    current = next[key]
  }
  return current
}

function endOfMonth(year: number, month: number): string {
  const lastDay = new Date(year, month, 0).getDate()
  return `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`
}

function getOverviewPerformanceWindow() {
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1
  return {
    from: `${year}-${String(month).padStart(2, "0")}-01`,
    to: endOfMonth(year, month),
    compare: "yoy" as const,
  }
}

function parsePropertySettingsSummary(settings: Record<string, unknown> | undefined): OverviewPropertySettingsSummary | null {
  const root = asRecord(settings)
  if (!root) return null

  const doorCode = asRecord(readPath(root, ["door_code"]))
  const propertyConfig = asRecord(readPath(root, ["property_config"]))

  return {
    wifiName: asString(readPath(root, ["wifi_name"])),
    wifiPasswordConfigured: Boolean(asString(readPath(root, ["wifi_password"]))),
    doorCodeConfigured: Boolean(asString(readPath(doorCode, ["door_code"]))),
    doorCodeShowGuidebook: asBoolean(readPath(doorCode, ["show_guidebook"]), true),
    doorCodeShowDisplay: asBoolean(readPath(doorCode, ["show_display"]), true),
    doorCodeOffsetMinutes: asNumber(readPath(doorCode, ["offset_minutes"])),
    guidebookPinConfigured: asBoolean(readPath(propertyConfig, ["guidebook_pin_configured"]), false),
    guidebookTemplate: asString(readPath(propertyConfig, ["guidebook_template"])),
    renderKey: asString(readPath(root, ["render_key"])) || asString(readPath(root, ["property", "render_key"])),
  }
}

function ruleEnabled(section: Record<string, unknown> | null, fallback = true): boolean {
  if (!section) return fallback
  if (!("enabled" in section)) return fallback
  return asBoolean(section.enabled, fallback)
}

function parseHouseRulesSummary(
  rules: Record<string, unknown> | undefined,
  updatedAt?: string,
): OverviewHouseRulesSummary | null {
  const root = asRecord(rules)
  if (!root) return null

  const checkin = asRecord(root.checkin)
  const quietHours = asRecord(root.quietHours)
  const parties = asRecord(root.parties)
  const checkout = asRecord(root.checkout)
  const smoking = asRecord(root.smoking)
  const pets = asRecord(root.pets)
  const visitors = asRecord(root.visitors)

  const policySections = [quietHours, parties, smoking, pets, visitors]
  const guidebookRules = policySections.filter((section) => {
    const surfaces = asRecord(section?.surfaces)
    return ruleEnabled(section, section === smoking || section === pets || section === visitors ? false : true)
      && asBoolean(readPath(surfaces, ["guidebook"]), true)
  }).length
  const displayRules = policySections.filter((section) => {
    const surfaces = asRecord(section?.surfaces)
    return ruleEnabled(section, false)
      && asBoolean(readPath(surfaces, ["display"]), false)
  }).length

  const enabledCoreRules = [
    ruleEnabled(checkin, true),
    ruleEnabled(quietHours, true),
    ruleEnabled(parties, true),
    ruleEnabled(checkout, true),
  ].filter(Boolean).length

  const summaryParts = [`${enabledCoreRules}/4 core rules ready`]
  if (guidebookRules > 0) {
    summaryParts.push(`${guidebookRules} guest-facing`)
  }
  if (displayRules > 0) {
    summaryParts.push(`${displayRules} on display`)
  }

  return {
    enabledCoreRules,
    displayRules,
    guidebookRules,
    quietHoursConfigured: ruleEnabled(quietHours, true),
    checkoutConfigured: ruleEnabled(checkout, true),
    summary: summaryParts.join(" · "),
    updatedAt: updatedAt || null,
  }
}

export function useOverviewData(): UseOverviewDataReturn {
  const { propertyId } = usePropertyContext()
  const [overview, setOverview] = useState<OverviewPayload | null>(null)
  const [insights, setInsights] = useState<InsightResult | null>(null)
  const [insightsStatus, setInsightsStatus] = useState<InsightsStatus>("loading")
  const [seasonContext, setSeasonContext] = useState<SeasonContext | null>(null)
  const [performanceSummary, setPerformanceSummary] = useState<PerformanceSummaryResponse | null>(null)
  const [performanceStatus, setPerformanceStatus] = useState<ResourceStatus>("idle")
  const [reviewIntelligence, setReviewIntelligence] = useState<ReviewIntelligenceResponse | null>(null)
  const [reviewStatus, setReviewStatus] = useState<ResourceStatus>("idle")
  const [propertySettings, setPropertySettings] = useState<OverviewPropertySettingsSummary | null>(null)
  const [propertySettingsStatus, setPropertySettingsStatus] = useState<ResourceStatus>("idle")
  const [houseRules, setHouseRules] = useState<OverviewHouseRulesSummary | null>(null)
  const [houseRulesStatus, setHouseRulesStatus] = useState<ResourceStatus>("idle")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const load = useCallback(async (silent = false) => {
    if (!propertyId) return

    if (!silent) {
      setLoading(true)
      setError("")
      setPerformanceSummary(null)
      setReviewIntelligence(null)
      setPropertySettings(null)
      setHouseRules(null)
      setPerformanceStatus("loading")
      setReviewStatus("loading")
      setPropertySettingsStatus("loading")
      setHouseRulesStatus("loading")
    } else {
      setPerformanceStatus((prev) => (prev === "ok" ? prev : "loading"))
      setReviewStatus((prev) => (prev === "ok" ? prev : "loading"))
      setPropertySettingsStatus((prev) => (prev === "ok" ? prev : "loading"))
      setHouseRulesStatus((prev) => (prev === "ok" ? prev : "loading"))
    }

    setInsightsStatus("loading")

    void apiClient.getPerformanceInsights(propertyId)
      .then((res) => {
        if (res.ok && res.result) {
          setInsights(res.result)
          setInsightsStatus("ok")
          setSeasonContext(res.season_context ?? null)
        } else {
          setInsights(null)
          setInsightsStatus("failed")
          setSeasonContext(null)
        }
      })
      .catch(() => {
        setInsights(null)
        setInsightsStatus("failed")
        setSeasonContext(null)
      })

    const performanceWindow = getOverviewPerformanceWindow()

    void apiClient.getPerformanceSummary(propertyId, performanceWindow)
      .then((res) => {
        if (res.ok) {
          setPerformanceSummary(res)
          setPerformanceStatus("ok")
        } else {
          setPerformanceSummary(null)
          setPerformanceStatus("failed")
        }
      })
      .catch(() => {
        setPerformanceSummary(null)
        setPerformanceStatus("failed")
      })

    void apiClient.getReviewIntelligence(propertyId)
      .then((res) => {
        if (res.ok) {
          setReviewIntelligence(res)
          setReviewStatus("ok")
        } else {
          setReviewIntelligence(null)
          setReviewStatus("failed")
        }
      })
      .catch(() => {
        setReviewIntelligence(null)
        setReviewStatus("failed")
      })

    void apiClient.getPropertySettings(propertyId)
      .then((res) => {
        if (res.ok) {
          setPropertySettings(parsePropertySettingsSummary(res.settings))
          setPropertySettingsStatus("ok")
        } else {
          setPropertySettings(null)
          setPropertySettingsStatus("failed")
        }
      })
      .catch(() => {
        setPropertySettings(null)
        setPropertySettingsStatus("failed")
      })

    void apiClient.getHouseRules(propertyId)
      .then((res) => {
        if (res.ok) {
          setHouseRules(parseHouseRulesSummary(res.rules, res.updated_at))
          setHouseRulesStatus("ok")
        } else {
          setHouseRules(null)
          setHouseRulesStatus("failed")
        }
      })
      .catch(() => {
        setHouseRules(null)
        setHouseRulesStatus("failed")
      })

    try {
      const res = await (apiClient.getPropertyOverview(propertyId) as Promise<OverviewResponse>)
      if (res.ok) {
        setOverview(res)
        if (!silent) setError("")
      } else if (!silent) {
        setError(res.error || "Unable to load overview data.")
      }
    } catch {
      if (!silent) setError("Unable to load overview data.")
    } finally {
      if (!silent) setLoading(false)
    }
  }, [propertyId])

  useEffect(() => {
    load(false)
  }, [load])

  const refresh = useCallback(() => {
    load(true)
  }, [load])

  return {
    overview,
    insights,
    insightsStatus,
    seasonContext,
    performanceSummary,
    performanceStatus,
    reviewIntelligence,
    reviewStatus,
    propertySettings,
    propertySettingsStatus,
    houseRules,
    houseRulesStatus,
    loading,
    error,
    refresh,
  }
}
